import React, { useState } from 'react';
import { Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useGetProductsQuery,
  useUpdateProductMutation,
  useUploadProductImagesMutation,
} from '../../slices/productsApiSlice';

const BulkProductUpdateWithImageUploadScreen = () => {
  const [updates, setUpdates] = useState([]);
  const [results, setResults] = useState([]);
  const [fileName, setFileName] = useState('');
  const { data, isLoading: loadingProducts } = useGetProductsQuery({ pageNumber: 1 });
  const [updateProduct, { isLoading: loadingUpdate }] = useUpdateProductMutation();
  const [uploadProductImages, { isLoading: loadingUpload }] = useUploadProductImagesMutation();

  const validateRow = (row) => {
    if (!row.Name) return 'Missing Name';
    if (isNaN(parseFloat(row.Price))) return 'Invalid Price';
    if (!row.Brand) return 'Missing Brand';
    if (!row.Category) return 'Missing Category';
    if (isNaN(parseInt(row.CountInStock))) return 'Invalid CountInStock';
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !data?.products) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = rows.map((row, index) => {
        const match = data.products.find(
          (p) => p.name.trim().toLowerCase() === row.Name.trim().toLowerCase()
        );
        return {
          rowIndex: index + 2,
          excelName: row.Name,
          matchedProduct: match,
          price: parseFloat(row.Price),
          brand: row.Brand,
          category: row.Category,
          countInStock: parseInt(row.CountInStock),
          images: row.Images ? row.Images.split(',').map((url) => url.trim()) : [],
          error: validateRow(row),
        };
      });

      setUpdates(enriched);
      setResults([]);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('images', file);

    try {
      const res = await uploadProductImages(formData).unwrap();
      const updated = [...updates];
      updated[index].images = res.images;
      setUpdates(updated);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.data?.message || err.message);
    }
  };

  const handleSubmit = async () => {
    const feedback = [];

    for (const row of updates) {
      if (row.error) {
        feedback.push({ index: row.rowIndex, status: 'Skipped', message: row.error });
        continue;
      }

      if (!row.matchedProduct) {
        feedback.push({ index: row.rowIndex, status: 'Skipped', message: 'No matching product found' });
        continue;
      }

      try {
        await updateProduct({
          productId: row.matchedProduct._id,
          name: row.matchedProduct.name,
          price: row.price,
          brand: row.brand,
          category: row.category,
          countInStock: row.countInStock,
          images: row.images,
          description: row.matchedProduct.description || 'Updated via Excel',
        }).unwrap();

        feedback.push({ index: row.rowIndex, status: 'Success', message: 'Updated' });
      } catch (err) {
        feedback.push({
          index: row.rowIndex,
          status: 'Failed',
          message: err?.data?.message || err.message,
        });
      }
    }

    setResults(feedback);
    toast.success('Update process completed');
  };

  return (
    <div className="container mt-4">
      <h2>🛠 Bulk Product Update with Image Upload</h2>

      <Form.Group controlId="excelUpload" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx" onChange={handleFileUpload} />
      </Form.Group>

      {updates.length > 0 && (
        <>
          <h5>Preview & Match: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Row</th>
                <th>Excel Name</th>
                <th>Matched Product</th>
                <th>Price</th>
                <th>Brand</th>
                <th>Category</th>
                <th>CountInStock</th>
                <th>Images</th>
                <th>Upload Image</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {updates.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td>{row.rowIndex}</td>
                  <td>{row.excelName}</td>
                  <td>{row.matchedProduct?.name || '❌ Not Found'}</td>
                  <td>{row.price}</td>
                  <td>{row.brand}</td>
                  <td>{row.category}</td>
                  <td>{row.countInStock}</td>
                  <td>
                    {row.images.map((img, i) => (
                      <img key={i} src={img} alt="preview" style={{ width: '20px', height: '20px', marginRight: '5px' }} />
                    ))}
                  </td>
                  <td>
                    <Form.Control type="file" onChange={(e) => handleImageUpload(e, idx)} />
                    {loadingUpload && <Spinner animation="border" size="sm" />}
                  </td>
                  <td>
                    {row.error
                      ? <span className="text-danger">{row.error}</span>
                      : row.matchedProduct
                      ? <span className="text-success">✅</span>
                      : <span className="text-warning">No Match</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button onClick={handleSubmit} disabled={loadingUpdate}>
            {loadingUpdate ? <Spinner animation="border" size="sm" /> : 'Submit Updates'}
          </Button>
        </>
      )}

      {results.length > 0 && (
        <div className="mt-4">
          <h5>📋 Update Results</h5>
          {results.map((r, i) => (
            <Alert key={i} variant={
              r.status === 'Success' ? 'success' :
              r.status === 'Skipped' ? 'warning' :
              'danger'
            }>
              Row {r.index}: {r.status} — {r.message}
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default BulkProductUpdateWithImageUploadScreen;